import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiClient, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, CheckCircle2, Workflow, Eye, MailCheck } from "lucide-react";

export default function LoginPage() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mfa, setMfa] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [resending, setResending] = useState(false);
  const [expiredMsg, setExpiredMsg] = useState("");

  useEffect(() => {
    try {
      const m = localStorage.getItem("ntn_session_expired_msg");
      if (m) { setExpiredMsg(m); localStorage.removeItem("ntn_session_expired_msg"); }
      else if (new URLSearchParams(window.location.search).get("expired")) {
        setExpiredMsg("Your session expired for security. Please sign in again.");
      }
    } catch (_e) { /* noop */ }
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await apiClient.post("/auth/login", { email, password });
      if (data.mfa_required) {
        setMfa({ challenge_token: data.challenge_token, email_masked: data.email_masked });
        setMfaCode("");
        setResendIn(60);
      } else {
        // Auth is cookie-based. No token to persist client-side.
        await refresh();
        toast.success(`Welcome back, ${data.name}`);
        navigate("/dashboard");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await apiClient.post("/auth/mfa/email/verify", {
        challenge_token: mfa.challenge_token, code: mfaCode,
        remember_device: rememberDevice,
      });
      // Auth is cookie-based. No token to persist client-side.
      await refresh();
      toast.success(`Welcome back, ${data.name}`);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setMfaCode("");
    } finally { setLoading(false); }
  };

  const resendCode = async () => {
    if (resendIn > 0 || resending) return;
    setResending(true); setError("");
    try {
      const { data } = await apiClient.post("/auth/mfa/email/resend", {
        challenge_token: mfa.challenge_token,
      });
      setMfa({ challenge_token: data.challenge_token, email_masked: data.email_masked });
      setMfaCode("");
      setResendIn(60);
      toast.success("A new code is on its way");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setResending(false); }
  };

  const idleNotice = params.get("reason") === "idle";

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      {/* Left — brand panel */}
      <div className="hidden lg:flex relative bg-slate-900 text-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 [background-image:radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.22),transparent_55%),radial-gradient(circle_at_80%_85%,rgba(139,92,246,0.14),transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 [background-image:linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)] [background-size:42px_42px] [mask-image:radial-gradient(ellipse_at_top,black,transparent_75%)] pointer-events-none" />

        <div className="relative z-10"><Logo light /></div>

        <div className="relative z-10 max-w-md space-y-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] tracking-[0.18em] uppercase font-semibold mb-5 bg-white/10 text-blue-200">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-300" />
              Verify Hub
            </div>
            <h2 className="font-serif text-4xl tracking-tight leading-tight">
              Turn applications into <span className="text-blue-300 italic">verified decisions.</span>
            </h2>
            <p className="mt-4 text-white/70 text-sm leading-relaxed">
              Verification, validation, and intelligent review — all in one operational platform.
            </p>
          </div>

          <ul className="space-y-3 text-sm text-white/80">
            {[
              { icon: ShieldCheck, label: "Applicant verification built to uncover risk" },
              { icon: Workflow,    label: "Comprehensive insight across the applicant file" },
              { icon: Eye,         label: "Track every applicant from screening through completion" },
            ].map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-md bg-white/10 flex items-center justify-center">
                  <Icon className="h-4 w-4 text-blue-300" />
                </div>
                {label}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative z-10 font-mono text-[10px] tracking-widest uppercase text-white/40">
          Secure sign-in · NTN-AUTH-V2
        </div>
      </div>

      {/* Right — form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10"><Logo /></div>

          {expiredMsg && (
            <div className="mb-5 rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                 data-testid="session-expired-banner">
              {expiredMsg}
            </div>
          )}

          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] tracking-[0.18em] uppercase font-semibold mb-5 bg-blue-50 text-blue-700">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
            {mfa ? "Verify Your Identity" : "Sign In"}
          </div>

          <h1 className="font-serif text-4xl text-slate-900 tracking-tight leading-tight mb-2">
            {mfa ? "Check your email." : "Access your account."}
          </h1>
          <p className="text-slate-600 text-sm mb-8">
            {mfa
              ? (
                <>
                  We sent a 6-digit verification code to{" "}
                  <span className="font-medium text-slate-900" data-testid="mfa-email-masked">{mfa.email_masked}</span>.
                </>
              )
              : (
                <>
                  New to NTN Verify Hub?{" "}
                  <Link to="/request-information" className="font-medium text-blue-600 hover:text-blue-700" data-testid="link-to-request-info">
                    Request Information
                  </Link>{" "}
                  or{" "}
                  <Link to="/schedule-demo" className="font-medium text-blue-600 hover:text-blue-700" data-testid="link-to-schedule-demo">
                    Schedule a Demo
                  </Link>
                </>
              )}
          </p>

          {idleNotice && !mfa && (
            <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800" data-testid="idle-notice">
              You were signed out due to inactivity. Please sign in again.
            </div>
          )}

          {!mfa ? (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              <div>
                <Label className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-600">Email</Label>
                <Input
                  data-testid="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 h-11 rounded-md bg-white border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-600">Password</Label>
                  <Link to="/forgot-password" className="text-xs font-medium text-blue-600 hover:text-blue-700" data-testid="link-forgot">
                    Forgot password?
                  </Link>
                </div>
                <Input
                  data-testid="login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 h-11 rounded-md bg-white border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>
              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-md" data-testid="login-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-semibold shadow-sm shadow-blue-600/20"
                data-testid="login-submit"
              >
                {loading ? "Signing in…" : <>Sign in <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
            </form>
          ) : (
            <form onSubmit={submitMfa} className="space-y-5" data-testid="mfa-form">
              <div>
                <Label htmlFor="mfa-code" className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-600">Verification code</Label>
                <Input
                  id="mfa-code"
                  autoFocus
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  onPaste={(e) => {
                    e.preventDefault();
                    const digits = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
                    setMfaCode(digits);
                  }}
                  aria-label="6-digit verification code"
                  className="mt-2 h-12 rounded-md bg-white text-2xl tracking-[0.5em] font-mono text-center border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  data-testid="mfa-code-input"
                />
              </div>
              <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="mfa-remember-device">
                <Checkbox
                  id="remember-device"
                  checked={rememberDevice}
                  onCheckedChange={(v) => setRememberDevice(v === true)}
                  className="mt-0.5"
                  data-testid="mfa-remember-device-checkbox"
                />
                <span>
                  <span className="block text-sm font-medium text-slate-800">Remember this device</span>
                  <span className="block text-xs text-slate-500 mt-0.5">
                    Skip verification codes on this trusted device for 30 days.
                  </span>
                </span>
              </label>
              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-md" role="alert" data-testid="mfa-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading || mfaCode.length !== 6}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-semibold"
                data-testid="mfa-submit"
              >
                {loading ? "Verifying…" : <>Verify <MailCheck className="ml-2 h-4 w-4" /></>}
              </Button>
              <div className="text-center text-sm text-slate-600">
                Didn't receive a code?{" "}
                <button
                  type="button"
                  onClick={resendCode}
                  disabled={resendIn > 0 || resending}
                  className={`font-medium ${resendIn > 0 || resending ? "text-slate-400 cursor-not-allowed" : "text-blue-600 hover:text-blue-700"}`}
                  data-testid="mfa-resend-btn"
                >
                  {resending ? "Sending…" : resendIn > 0 ? `Resend Code (${resendIn}s)` : "Resend Code"}
                </button>
              </div>
              <Button
                type="button"
                variant="ghost"
                onClick={() => { setMfa(null); setMfaCode(""); setError(""); setResendIn(0); }}
                className="w-full rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                data-testid="mfa-back"
              >
                Back to sign in
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
